'use client';

import { Form, Formik } from 'formik';
import { debounce, isEqual } from 'lodash';
import { useRouter } from 'next/navigation';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';

import {
  PageProductListingFilters,
  PageProductListingOptions,
} from 'src/data/Contentful/ContentfulGetPageProductListing.types';
import { ScrollBox } from 'src/general/components/ScrollBox/ScrollBox';
import { plpStore } from 'src/stores/plpStore';
import { getPlpUrlFromStoreFilters } from 'src/utils/plp/getPlpUrlFromStoreFilters';

import { PlpFilterFormObserver } from './PlpFilterFormObserver';
import { PlpFilters } from './PlpFilters';
import { FilterOptions, PLP_FILTER_SUBMIT_DEBOUNCE } from './PlpFilters.type';

interface Props {
  filterOptions: FilterOptions;
  driveTrainHelper?: ReactNode;
}

export const PlpFiltersWrapperSidebar: FC<Props> = ({ filterOptions, driveTrainHelper }) => {
  const router = useRouter();
  const { currentFilters, currentListingOptions } = plpStore(
    (state) => ({
      currentFilters: state.currentFilters,
      currentListingOptions: state.currentListingOptions,
    }),
    shallow,
  );

  const changeHandler = useCallback(
    (values: PageProductListingFilters, options?: PageProductListingOptions) => {
      if (!isEqual(values, currentFilters)) {
        const url = getPlpUrlFromStoreFilters({ ...values, ...options });
        router.push(url);
      }
    },
    [currentFilters, currentListingOptions, router],
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, PLP_FILTER_SUBMIT_DEBOUNCE), [changeHandler]);

  const onSubmit = useCallback(() => {
    // Submit method not required as the FormObserver handles the filter updates
  }, []);

  return (
    <ScrollBox className="filtersWrapper">
      <Formik initialValues={currentFilters} enableReinitialize onSubmit={onSubmit}>
        <Form>
          <PlpFilterFormObserver
            filters={currentFilters}
            listingOptions={currentListingOptions}
            setFilters={debouncedChangeHandler}
          />
          <PlpFilters
            idPrepend="desktop"
            type="default"
            filterOptions={filterOptions}
            driveTrainHelper={driveTrainHelper}
          />
        </Form>
      </Formik>
    </ScrollBox>
  );
};
